/**
 * TransferDeviceModal
 *
 * Allows the user to move a Kasa device to a different organization
 * or to their personal namespace. The device disappears from the current
 * context after a successful transfer.
 */

import React, { useState } from 'react';
import { useKasaStore, KasaDevice } from '../kasa-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRightLeft, Info, Loader2 } from 'lucide-react';
import * as errorUtils from '@/utils/errors';

interface TransferDeviceModalProps {
  device: KasaDevice;
  isOpen: boolean;
  onClose: () => void;
}

export const TransferDeviceModal: React.FC<TransferDeviceModalProps> = ({
  device,
  isOpen,
  onClose,
}) => {
  const { transferDevice, error, clearError } = useKasaStore();
  const { organizations, currentContext } = useOrganizationStore();

  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const currentOrgSlug =
    currentContext?.type === 'organization' ? currentContext.orgSlug : null;

  // Build target options: personal (if in org context) + all other orgs
  const targets: Array<{ label: string; value: string; type: 'personal' | 'org' }> = [
    ...(currentOrgSlug
      ? [{ label: 'Personal', value: 'personal', type: 'personal' as const }]
      : []),
    ...organizations
      .filter((org) => org.slug !== currentOrgSlug)
      .map((org) => ({ label: org.name, value: org.slug, type: 'org' as const })),
  ];

  const handleTransfer = async () => {
    if (!selectedTarget) return;
    setIsTransferring(true);
    setLocalError(null);
    clearError();

    try {
      const target =
        selectedTarget === 'personal'
          ? { personal: true as const }
          : { orgSlug: selectedTarget };
      await transferDevice(device.host, target);
      onClose();
    } catch (err: unknown) {
      setLocalError(errorUtils.getErrorMessageOrFallback(err, 'Transfer failed'));
    } finally {
      setIsTransferring(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedTarget(null);
      setLocalError(null);
      clearError();
      onClose();
    }
  };

  const displayError = localError || error;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" />
            Transfer Device
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Move <span className="font-medium text-foreground">{device.alias}</span> to a
            different organization or your personal namespace. The device will be removed
            from the current context.
          </p>

          {device.protocol === 'klap' && (
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Your TP-Link account credentials will be shared with the target if they
                haven&apos;t set up Kasa yet.
              </span>
            </div>
          )}

          {targets.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No other organizations available to transfer to.
            </p>
          ) : (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Transfer to
              </p>
              <div className="space-y-1">
                {targets.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setSelectedTarget(t.value)}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                      selectedTarget === t.value
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border hover:border-muted-foreground/50 hover:bg-muted/50'
                    }`}
                  >
                    {t.label}
                    {t.type === 'personal' && (
                      <span className="ml-2 text-xs text-muted-foreground">(your account)</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {displayError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {displayError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isTransferring}>
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!selectedTarget || isTransferring || targets.length === 0}
          >
            {isTransferring ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Transferring…
              </>
            ) : (
              'Transfer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
